import React, { useCallback, useMemo, useState } from 'react';

import find from 'lodash/find';

import Button from '../../../common/Button/Button';
import Label from '../../../common/Labels/Label';
import LinkButton from '../../../common/Labels/LinkButton';
import SearchInput from '../../../common/TextInputs/SearchInput/SearchInput';

import EdgeLocationFilterList from '../EdgeLocationFilterList/EdgeLocationFilterList';

export const EdgeLocationFilterControls = ({
  currentSelections,
  edgeLocations,
  searchQuery,
  setSearchQuery,
  setCurrentSelections,
}) => {
  const filteredEdgeLocations = useMemo(() => edgeLocations?.filter(({ name }) => {
    if (!name?.length) {
      return false;
    }
    if (!searchQuery?.length) {
      return true;
    }
    return name.toLowerCase().includes(searchQuery.toLowerCase())
  }), [edgeLocations, searchQuery]);

  const onQueryChange = useCallback((event) => {
    setSearchQuery(event?.target?.value || '');
  }, [setSearchQuery])

  const selectAllLocations = useCallback(() => {
    setCurrentSelections([...filteredEdgeLocations]);
  }, [filteredEdgeLocations, setCurrentSelections])

  const deselectAllLocations = useCallback(() => {
    setCurrentSelections([]);
  }, [setCurrentSelections]);

  // Update selection state for
  const toggleSelectionForEdge = useCallback((edgeId) => {
    const selectedLocation = find(edgeLocations, ({ id }) => id === edgeId);
    const hasLocation = find(currentSelections, ({ id }) => id === selectedLocation.id);
    if (!hasLocation) {
      setCurrentSelections([
        ...currentSelections,
        selectedLocation
      ])
    } else {
      const withoutSelected = currentSelections.filter(({ id }) => id !== edgeId);
      setCurrentSelections(withoutSelected);
    }
  }, [currentSelections, edgeLocations, setCurrentSelections]);

  return (
    <div className="overflow-y-scroll m-1">
      <SearchInput
        className="mb-4"
        value={searchQuery}
        onChange={onQueryChange}
        ariaLabel="Search by location name"
        placeholder="Search by location name"
        block
      />
      <div className="flex items-center justify-start m-1">
        <LinkButton
          className="mr-8"
          color="link-blue"
          hoverColor="faded-brand"
          activeColor="highlighted"
          onClick={selectAllLocations}
        >
          Select All Locations
        </LinkButton>
        <LinkButton
          color="link-red"
          hoverColor="faded-error"
          activeColor="error"
          onClick={deselectAllLocations}
        >
          Deselect All
        </LinkButton>
      </div>
      <EdgeLocationFilterList
        selectedLocations={currentSelections}
        edgeLocations={filteredEdgeLocations}
        onSelectionChanged={toggleSelectionForEdge}
        disableScrollFading
      />
    </div>
  )
}

export const EdgeLocationFilterPanel = ({ 
  edgeLocations = [],
  selectedEdgeLocations = [],
  onCancel = () => {},
  onSubmit = () => {} 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentSelections, setCurrentSelections] = useState(selectedEdgeLocations);

  return (
    <div className="flex flex-col w-full m-1">
      <div className="flex justify-between items-center mb-2">
        <Label size="lg" bold>Filter Edge Locations</Label>
        <Button
          variant="close"
          iconSize={24}
          textColor="brand"
          onClick={onCancel}
        />
      </div>
      <EdgeLocationFilterControls
        edgeLocations={edgeLocations}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        currentSelections={currentSelections}
        setCurrentSelections={setCurrentSelections}
      />
      <Button variant="primary" onClick={() => onSubmit(currentSelections)}>Filter</Button>
    </div>
  )
}

export default EdgeLocationFilterPanel;
